import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '@/services/api';
import { UserLocation } from '@/types/location.types';
import { logger } from '@/utils/logger';
import { longCacheQueryOptions } from '@/config/react-query.config';

interface UseUserLocationReturn {
  location: UserLocation | null;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Hook to fetch and store user's location
 * Automatically stores location in localStorage for persistence
 */
export const useUserLocation = (): UseUserLocationReturn => {
  const queryOptions = longCacheQueryOptions as Partial<
    UseQueryOptions<UserLocation, Error>
  >;

  const { data, isLoading, error, refetch } = useQuery<UserLocation, Error>({
    queryKey: ['user-location'],
    /* The stored copy seeds the cache; the queryFn always hits the API.
       Reading storage inside the queryFn made invalidation a no-op, so a
       bookingStartDate set or reached after login stayed invisible until
       the member logged out (ENG-747). */
    initialData: () => getUserLocationFromStorage() ?? undefined,
    initialDataUpdatedAt: 0,
    queryFn: (): Promise<UserLocation> =>
      api.get('/member-locations/my-location').then((response) => {
        const location = response.data;

        // Store location in localStorage with cache version
        localStorage.setItem('userLocation', JSON.stringify(location));
        localStorage.setItem('userLocationVersion', '4');

        return location;
      }),
    enabled: !!localStorage.getItem('token'),
    retryOnMount: false,
    ...queryOptions,
    // Only the stored seed is stale on mount, so this costs one read per launch.
    refetchOnMount: true,
  });

  return {
    location: data ?? null,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Get user location from localStorage (synchronous)
 */
export const getUserLocationFromStorage = (): UserLocation | null => {
  try {
    const locationStr = localStorage.getItem('userLocation');
    if (locationStr) {
      const location = JSON.parse(locationStr);
      // Discard stale cache missing the GHL CRM locationId — payment config
      // lookups require it; the MongoDB id fallback doesn't match locationCustomId.
      if (!location.locationId) {
        localStorage.removeItem('userLocation');
        return null;
      }
      // Discard pre-v4 caches (v3 lacked supportEmail, v2 lacked boxPro /
      // defaultPhoneNumber — all needed for the support contact links).
      if (localStorage.getItem('userLocationVersion') !== '4') {
        localStorage.removeItem('userLocation');
        return null;
      }
      return location;
    }
    return null;
  } catch (error) {
    logger.error('Error parsing user location from localStorage:', error);
    return null;
  }
};

/**
 * Clear user location from localStorage
 */
export const clearUserLocationFromStorage = (): void => {
  localStorage.removeItem('userLocation');
  localStorage.removeItem('userLocationVersion');
};

export type { UserLocation } from '@/types/location.types';
